import React, { createContext, useContext, useState, PropsWithChildren } from "react";

/**
 * Interface for the role context value
 */
interface RoleContextValue {
	// The actual role from the server (Admin, Client, etc.)
	actualRole: string;
	// The effective role for rendering (can be toggled by admins to "Client" mode)
	effectiveRole: string;
	// Whether the admin is currently viewing in client mode
	isClientMode: boolean;
	// Toggle client mode (only works for admins)
	toggleClientMode: () => void;
	// Set the actual role (called on login/load)
	setActualRole: (role: string) => void;
	// Check if user is actually an admin (regardless of client mode)
	isActuallyAdmin: () => boolean;
}

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

/**
 * Provider component for role context - manages actual role and client mode toggle
 */
export function RoleProvider({ children }: PropsWithChildren<{}>) {
	const [actualRole, setActualRole] = useState<string>("");
	const [isClientMode, setIsClientMode] = useState<boolean>(false);

	// Effective role is "Client" when admin toggles to client mode, otherwise actual role
	const effectiveRole = isClientMode && actualRole === "Admin" ? "Client" : actualRole;

	const toggleClientMode = () => {
		// Only admins can toggle client mode
		if (actualRole === "Admin") {
			setIsClientMode((prev) => !prev);
		}
	};

	const isActuallyAdmin = () => actualRole === "Admin";

	const value: RoleContextValue = {
		actualRole,
		effectiveRole,
		isClientMode,
		toggleClientMode,
		setActualRole,
		isActuallyAdmin,
	};

	return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

/**
 * Hook to access the role context
 */
export function useRole(): RoleContextValue {
	const context = useContext(RoleContext);
	if (context === undefined) {
		throw new Error("useRole must be used within a RoleProvider");
	}
	return context;
}
